import { GridColDef, useGridApiRef } from '@mui/x-data-grid-pro';
import React, { useMemo } from 'react';
import {
  copyBtn,
  createdAt,
  enteredAt,
  exitedAt,
  checkByStatus,
  researchProject,
  sampleId,
  sampleIdentifier,
  stGridSampleAvailability,
  stGridSampleSampleTypeId,
  transition,
  wallClock,
  qcStatus,
} from '../../util/Constants';
import { makeStyles } from '@mui/styles';
import { Transition, TransitionSampleDetailsDto } from '../../data/SampleTrackingTransitionData';
import useMemoTranslation from '../../hooks/UseMemoTranslation';
import { Box } from '@mui/material';
import { Dictionary, UseStateSetter } from '../../util/TypeUtil';
import { TransitionSample } from '../../data/SampleTrackingData';
import { dateGetter, sampleTrackingGridDefaultColumnOverrides } from '../../util/grid/TableUtils';
import { GridCellCallBack } from '../../components/grid/GridCellCallback';
import { keyBy } from 'lodash';
import { Sample } from '../../data/SampleData';
import { SampleTransitionInformation } from '../../data/SampleHierarchyData';
import { LoadingProps } from '../../components/LoadingStateUtil';
import { SampleTrackingGridWrapper } from '../../components/grid/SampleTrackingGridWrapper';
import { renderCopyTextGridCell } from '../../components/grid/cell/CopyTextGridCell';
import { ConfiguredTransition } from '../../data/ConfiguredTransitionData';
import { TextOverflowBox } from 'components/TextOverflowBox';
import { useSampleTrackingConfig } from '../../contexts/SampleTrackingConfigContext';

export interface SampleHistoryGridProps {
  identifiersByTransitionSampleId: Dictionary<string>;
  transitionSample: ReadonlyArray<TransitionSampleDetailsDto>;
  transitionInformation: ReadonlyArray<SampleTransitionInformation>;
  selectedSampleIds: ReadonlyArray<string>;
  setSelectedSampleIds: UseStateSetter<ReadonlyArray<string>>;
  filterOutInputOrders: boolean;
  showGlobalHistory: boolean;
  samples: ReadonlyArray<Sample>;
  configuredTransitions: ReadonlyArray<ConfiguredTransition>;
  loadingProps: LoadingProps;
  rightActions?: React.ReactNode;
  leftActions?: React.ReactNode;
  leafSampleIds: ReadonlyArray<string>;
  completedSampleIds: ReadonlyArray<string>;
}

export type Row = {
  id: number;
  sampleIdentifier: string;
  transitionSampleCreatedIn?: string;
  checkByCreatedAt?: Date;
  researchProject: string;
} & Transition &
  TransitionSample &
  Sample;

const useStyles = makeStyles({
  highlightedRow: {
    backgroundColor: 'rgb(231, 221, 255)',
  },
  noHover: {
    '&:hover': {
      backgroundColor: 'inherit !important',
    },
    '& .MuiDataGrid-cell': {
      cursor: 'default',
    },
  },
  completedRow: {
    backgroundColor: '#e3f2fd',
    '&:hover': {
      backgroundColor: '#e3f2fd !important',
    },
  },
});

/** Unique identifier for this component. Used for view management persistence. Should not be changed once set. */
const COMPONENT_NAME = 'SampleHistoryGrid';

export const SampleHistoryGrid = ({
  identifiersByTransitionSampleId,
  transitionSample,
  transitionInformation,
  selectedSampleIds,
  setSelectedSampleIds,
  filterOutInputOrders,
  showGlobalHistory,
  samples,
  configuredTransitions,
  loadingProps,
  rightActions,
  leftActions,
  leafSampleIds,
  completedSampleIds,
}: SampleHistoryGridProps) => {
  const classes = useStyles();
  const apiRef = useGridApiRef();

  const { configuredTransitionId, transitionEnumId, researchProjectId } = useSampleTrackingConfig();

  const columns = useColumns(selectedSampleIds, identifiersByTransitionSampleId, setSelectedSampleIds);
  const rows = useRows(
    researchProjectId,
    transitionSample,
    identifiersByTransitionSampleId,
    filterOutInputOrders,
    showGlobalHistory,
    transitionInformation,
    samples,
    configuredTransitions,
    leafSampleIds,
    completedSampleIds,
    selectedSampleIds,
    configuredTransitionId
  );

  return (
    <SampleTrackingGridWrapper
      apiRef={apiRef}
      rows={rows}
      columns={columns}
      initialState={{
        columns: {
          columnVisibilityModel: {
            foreignHash: false,
            sampleBbid: false,
          },
        },
        sorting: {
          sortModel: [
            { field: createdAt, sort: 'asc' },
            { field: enteredAt, sort: 'asc' },
          ],
        },
      }}
      defaultGroupingExpansionDepth={100}
      disableRowSelectionOnClick
      getRowClassName={params => {
        if (completedSampleIds && completedSampleIds.includes(params.row.sampleId)) {
          return classes.completedRow;
        } else if (selectedSampleIds && selectedSampleIds.includes(params.row.sampleId)) {
          return classes.highlightedRow;
        }
        return classes.noHover;
      }}
      leftActions={leftActions}
      rightActions={rightActions}
      loadingProps={loadingProps}
      hideLoadingIndicator
      defaultColumnOrdering={[
        copyBtn,
        sampleId,
        researchProject,
        transition,
        checkByStatus,
        qcStatus,
        enteredAt,
        exitedAt,
        stGridSampleSampleTypeId,
        stGridSampleAvailability,
      ]}
      cacheKey={'sample-tracking-history-grid'}
      columnDefinitionOverrides={sampleTrackingGridDefaultColumnOverrides}
      componentName={COMPONENT_NAME}
      configuredTransitionId={configuredTransitionId}
      transitionEnumId={transitionEnumId}
      researchProjectId={researchProjectId}
    />
  );
};

const useRows = (
  researchProjectId: string,
  transitionSampleData: ReadonlyArray<TransitionSampleDetailsDto>,
  identifiersByTransitionSampleId: Dictionary<string>,
  filterOutInputOrders: boolean,
  showGlobalHistory: boolean,
  transitionInformation: ReadonlyArray<SampleTransitionInformation>,
  samples: ReadonlyArray<Sample>,
  configuredTransitions: ReadonlyArray<ConfiguredTransition>,
  leafSampleIds: ReadonlyArray<string>,
  completedSampleIds: ReadonlyArray<string>,
  selectedSampleIds: ReadonlyArray<string>,
  configuredTransitionId?: string
): ReadonlyArray<Row> => {
  return useMemo(() => {
    if (transitionSampleData.length === 0) {
      return [];
    }

    const validConfiguredTransitionIds = configuredTransitionId
      ? configuredTransitions
          .filter(transition => {
            const selectedTransition = configuredTransitions.find(
              t => t.configuredTransitionId === configuredTransitionId
            );
            return selectedTransition && transition.transitionOrder <= selectedTransition.transitionOrder;
          })
          .map(t => t.configuredTransitionId)
      : [];

    const informationBySampleId = keyBy(transitionInformation, t => t.sampleId);
    const samplesBySampleId = keyBy(samples, t => t.sampleId);
    const transitionById = keyBy(configuredTransitions, i => i.configuredTransitionId);

    return (
      transitionSampleData
        .filter(r => (showGlobalHistory ? true : r.researchProject.researchProjectId === researchProjectId))
        .filter(r => {
          // Don't filter transitions that are not in the current project when showGlobalHistory is true
          if (showGlobalHistory && r.researchProject.researchProjectId !== researchProjectId) return true;
          if (!configuredTransitionId && validConfiguredTransitionIds.length === 0) return true;
          // Filter out transitions that are after the selected transition
          return validConfiguredTransitionIds.includes(r.transition.configuredTransitionId);
        })
        .map((r, index) => {
          const information = informationBySampleId[r.transitionSample.sampleId];
          const sample = samplesBySampleId[r.transitionSample.sampleId];

          return {
            id: index,
            sampleIdentifier: identifiersByTransitionSampleId[r.transitionSample.transitionSampleId],
            transitionSampleCreatedIn: information.transitionSampleCreatedIn?.transitionSampleId,
            checkByCreatedAt: information.transitionSampleCreatedIn?.checkByCreatedAt,
            researchProject: r.researchProject.name,
            researchProjectId: r.researchProject.researchProjectId,
            ...r.transitionSample,
            ...r.transition,
            ...sample,
            transition:
              transitionById[r.transition.configuredTransitionId ?? '']?.displayName ?? r.transition.transition,
            isCurrent:
              r.transitionSample.transitionSampleId === information.currentTransitionSample?.transitionSampleId,
            isLeaf: leafSampleIds.includes(sample.sampleId),
          };
        })
        // Show all transitions the sample has exited. The current. The root.
        .filter(
          i =>
            // If filter is disabled then pass all through
            !filterOutInputOrders ||
            // if sample has exited order then show
            i.exitedAt ||
            // if sample is the latest transition then show
            (i.isCurrent && i.isLeaf)
        )
        .filter(
          r =>
            !(
              // Prevent showing completed samples that are not selected
              (
                completedSampleIds.includes(r.sampleId) &&
                !selectedSampleIds.includes(r.sampleId) &&
                r.exitedAt &&
                ['Passed', 'Failed'].includes(r.checkByStatus)
              )
            )
        )
    );
  }, [
    researchProjectId,
    transitionSampleData,
    identifiersByTransitionSampleId,
    filterOutInputOrders,
    showGlobalHistory,
    transitionInformation,
    samples,
    configuredTransitions,
    leafSampleIds,
    completedSampleIds,
    selectedSampleIds,
    configuredTransitionId,
  ]);
};

const useColumns = (
  selectedSampleIds: ReadonlyArray<string>,
  identifiersBySampleId: Dictionary<string>,
  setSelectedSampleIds: UseStateSetter<ReadonlyArray<string>>
): GridColDef[] => {
  const { t } = useMemoTranslation();

  return useMemo(() => {
    if (Object.keys(identifiersBySampleId).length === 0) {
      return [];
    }

    return [
      {
        field: copyBtn,
        headerName: t(copyBtn),
        headerAlign: 'center',
        align: 'center',
        maxWidth: 30,
        renderCell: renderCopyTextGridCell,
        valueGetter: params => params.row.sampleIdentifier,
      },
      {
        field: sampleId,
        headerName: t(sampleIdentifier),
        headerAlign: 'left',
        align: 'left',
        minWidth: 250,
        cellClassName: 'monospace-font',
        renderCell: params => {
          const { value, row } = params;
          const sampleId = row?.sampleId;

          return (
            <Box sx={{ fontFamily: 'monospace', display: 'inline-block', width: 'inherit' }}>
              {selectedSampleIds.includes(sampleId) ? (
                <TextOverflowBox value={value} />
              ) : (
                <GridCellCallBack
                  text={value}
                  callBack={() => {
                    setSelectedSampleIds([sampleId]);
                  }}
                  sx={{ paddingLeft: 0, minWidth: 0, fontFamily: 'monospace' }}
                />
              )}
            </Box>
          );
        },
        valueGetter: params => params.row.sampleIdentifier,
        key: selectedSampleIds,
      },
      {
        field: researchProject,
        headerName: t(researchProject),
        headerAlign: 'left',
        align: 'left',
        minWidth: 150,
      },
      {
        field: transition,
        headerName: t(transition),
        headerAlign: 'left',
        align: 'left',
        width: 150,
        valueFormatter: ({ value }) => value && t(value),
      },
      {
        field: checkByStatus,
        headerName: t(checkByStatus),
        headerAlign: 'left',
        align: 'left',
        width: 150,
        valueFormatter: ({ value }) => value && t(value),
      },
      {
        field: qcStatus,
        headerName: t(qcStatus),
        headerAlign: 'left',
        align: 'left',
        minWidth: 100,
        valueFormatter: ({ value }) => value && t(value),
      },
      {
        field: enteredAt,
        headerName: t(enteredAt),
        headerAlign: 'left',
        align: 'left',
        minWidth: 100,
        type: 'date',
        valueGetter: dateGetter,
      },
      {
        field: exitedAt,
        headerName: t(exitedAt),
        headerAlign: 'left',
        align: 'left',
        minWidth: 100,
        type: 'date',
        valueGetter: dateGetter,
      },
      {
        field: createdAt,
        headerName: t(wallClock),
        headerAlign: 'left',
        align: 'left',
        minWidth: 100,
        type: 'date',
        valueGetter: dateGetter,
      },
    ];
  }, [t, selectedSampleIds, identifiersBySampleId, setSelectedSampleIds]);
};
